import { Link } from "@quintype/components";
import React, { useState } from "react";

import Close from "../../../../../atoms/other-icons/Close";
import PVLogo from "../../../../../atoms/other-icons/PVLogo";
import Search from "../../../../../atoms/other-icons/Search";
import SocialShareIcons from "../../../../../molecules/social-share";
import AccLoginBtn from "../../../../../atoms/acc-login";
import NewsletterBtn from "../../../../../atoms/newsletter-btn";

import {
  FOLLOW_US_TEXT,
  SEARCH_PLACEHOLDER
} from "../../../../../../constants";

import "./hamburger.m.css";
import DesktopHamburgerMenuItem from "./DesktopHamburgerMenuItem";

const HamburgerPage = ({
  primaryMenus,
  secondaryMenus,
  signinHandler,
  onAccountHandler,
  closeHandler,
  currentPath,
}) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [visibleIndex, setVisibleIndex] = useState(null);
  const [navSearchText, setNavSearchText] = useState("");
  const closingTimer = React.useRef(null);
  const submenuRef = React.useRef(null);

  const toggleMenu = (index) => {
    if (closingTimer.current) clearTimeout(closingTimer.current);

    if (activeIndex === index) {
      // Animate close by measuring actual height then transitioning to 0
      const el = submenuRef.current;
      if (el) {
        const height = el.scrollHeight;
        el.style.height = `${height}px`;
        el.getBoundingClientRect(); // force reflow so browser registers the start height
        el.style.transition = "height 0.35s ease-in, opacity 0.3s ease-in";
        el.style.height = "0px";
        el.style.opacity = "0";
      }
      setActiveIndex(null);
      closingTimer.current = setTimeout(() => {
        setVisibleIndex(null);
      }, 380);
    } else {
      // Opening a new submenu (or switching)
      setActiveIndex(index);
      setVisibleIndex(index);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && navSearchText.trim()) {
      window.location.href = `/search?q=${navSearchText}`;
    }
  };

  // Helper to extract path from URL
  const extractPath = (url) => {
    if (!url) return '';
    try {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url);
        return urlObj.pathname;
      }
      return url;
    } catch (e) {
      return url;
    }
  };

  // Store which parent menu was clicked when a child is clicked
  const handleChildClick = (childUrl, parentMenu, parentIndex) => {
    try {
      const urlPath = extractPath(childUrl);
      const cleanUrl = urlPath.split('?')[0].split('#')[0];
      const isPrimary = parentIndex < primaryMenus.length;
      sessionStorage.setItem('clickedMenu', JSON.stringify({
        url: cleanUrl,
        menuId: parentMenu.id,
        type: isPrimary ? 'primary' : 'secondary'
      }));
    } catch (e) {
      // Ignore storage errors
    }
  };

  const allMenus = primaryMenus;

  const rows = Array.from(
    { length: Math.ceil(allMenus.length / 3) },
    (_, i) => allMenus.slice(i * 3, i * 3 + 3)
  );

  return (
    <div styleName="hamburger-page">

      {/* HEADER */}
      <div styleName="hamburger-header">
        <div styleName="logo">
          <Link href="/">
            <PVLogo />
          </Link>
        </div>

        <div styleName="actions">
          <div styleName="search-input-wrapper">
            <input
              type="text"
              placeholder={SEARCH_PLACEHOLDER}
              styleName="search-input"
              value={navSearchText}
              onChange={(e) => setNavSearchText(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />

            <Link
              href={`/search?q=${navSearchText}`}
              styleName="search-icon-btn"
              aria-label="Search"
            >
              <Search />
            </Link>
          </div>

          <AccLoginBtn signinHandler={signinHandler} onAccountHandler={onAccountHandler} />
          <NewsletterBtn />

          <div onClick={closeHandler} styleName="close-button">
            <Close />
          </div>
        </div>
      </div>

      {/* MENU GRID */}
      <div styleName="menu-grid">
        {rows.map((row, rowIdx) => {
          const baseIdx = rowIdx * 3;
          const isActiveRow = row.some((_, colIdx) => activeIndex === baseIdx + colIdx);
          const isVisibleRow = row.some((_, colIdx) => visibleIndex === baseIdx + colIdx);
          const showSubmenu = isActiveRow || isVisibleRow;
          const submenuIndex = isActiveRow ? activeIndex : visibleIndex;

          return (
            <React.Fragment key={`row-${rowIdx}`}>
              {row.map((item, colIdx) => {
                const idx = baseIdx + colIdx;
                const isActive = activeIndex === idx;
                const isPrimary = true;

                return (
                  <DesktopHamburgerMenuItem
                    key={idx}
                    item={item}
                    index={idx}
                    isActive={isActive}
                    toggleMenu={toggleMenu}
                    currentPath={currentPath}
                    allMenus={allMenus}
                    currentIndex={idx}
                    menuType={isPrimary ? 'primary' : 'secondary'}
                    primaryMenuCount={primaryMenus.length}
                  />
                );
              })}

              {showSubmenu && submenuIndex !== null && (
                <div ref={submenuRef} styleName="submenu-wrapper">
                  <div styleName="submenu-grid">
                    {(allMenus[submenuIndex].children ?? []).map((sub, i, arr) => {
                      const totalItems = arr.length;
                      const totalRows = Math.ceil(totalItems / 3);
                      const currentRow = Math.floor(i / 3) + 1;
                      const isLastRow = currentRow === totalRows;

                      return (
                        <Link
                          key={i}
                          href={sub.completeUrl}
                          styleName={`submenu-item ${isLastRow ? 'last-in-column' : ''}`}
                          onMouseDown={() => handleChildClick(sub.completeUrl, allMenus[submenuIndex], submenuIndex)}
                        >
                          {sub.title}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* FOOTER */}
      <div styleName="menu-footer">
        {FOLLOW_US_TEXT}
        <SocialShareIcons />
      </div>
    </div>
  );
};

export default HamburgerPage;
